import { ABBR_MUSCLE, FN_POOL, ANTO } from './drillData';

// Levenshtein distance
export function lev(a, b) {
  const m = a.length, n = b.length;
  if (!m) return n;
  if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  let cur = new Array(n + 1);
  for (let i = 1; i <= m; i++) {
    cur[0] = i;
    for (let j = 1; j <= n; j++) {
      const c = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + c);
    }
    [prev, cur] = [cur, prev];
  }
  return prev[n];
}

export function allowedDist(len) {
  return Math.min(3, Math.max(1, Math.floor(len * 0.2)));
}

export function fuzzyHit(input, targets) {
  if (!input) return false;
  for (const t of targets) {
    if (!t) continue;
    if (input === t) return true;
    if (Math.abs(input.length - t.length) <= 3 && lev(input, t) <= allowedDist(t.length)) return true;
  }
  return false;
}

export function normNerve(s) {
  return (s || "").toLowerCase().replace(/[()]/g, " ").replace(/[^a-z0-9]+/g, " ").trim().replace(/\s+/g, " ");
}

export function normMuscle(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim().replace(/\s+/g, " ");
}

export function canonRoot(s) {
  let t = (s || "").toUpperCase().replace(/[\u2013\u2014]/g, "-");
  if (/SEG/.test(t)) return "SEGMENTAL";
  t = t.replace(/(\d)([A-Z])/g, "$1-$2").replace(/\bTO\b/g, "-").replace(/[\s,/]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
  if (!t) return "";
  const parts = t.split("-").filter(Boolean);
  let last = "";
  const norm = parts.map(p => {
    const m = p.match(/^([A-Z]*)(\d+)$/);
    if (!m) return p;
    const letter = m[1] || last;
    if (m[1]) last = m[1];
    return letter + m[2];
  });
  return norm.length === 1 ? norm[0] : norm[0] + "-" + norm[norm.length - 1];
}

export function nerveAccepts(display, extra) {
  const exact = new Set();
  const add = s => { const n = normNerve(s); if (n) exact.add(n); };
  add(display);
  const noParen = display.replace(/\(.*?\)/g, " ").trim();
  add(noParen);
  const pm = display.match(/\(([^)]*)\)/);
  if (pm) add(pm[1]);
  noParen.split(/\s+or\s+/i).forEach(add);
  noParen.split(/\s+and\s+/i).forEach(add);
  (extra || []).forEach(add);
  if (normNerve(display).split(" ").includes("pin")) add("posterior interosseous");
  return { exact, fuzzy: [normNerve(display), normNerve(noParen)] };
}

export function shuffle(a) {
  const arr = a.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// Build antonym map
const ANTO_MAP = {};
ANTO.forEach(([a, b]) => { ANTO_MAP[a] = b; ANTO_MAP[b] = a; });
const ANTO_KEYS = Object.keys(ANTO_MAP).sort((x, y) => y.length - x.length);
const ANTO_RE = new RegExp(
  "\\b(" + ANTO_KEYS.map(k => k.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")).join("|") + ")\\b", "gi"
);

function matchCase(o, r) {
  return (o[0] === o[0].toUpperCase()) ? r[0].toUpperCase() + r.slice(1) : r;
}

export function opposite(str) {
  let ch = false;
  const s = str.replace(ANTO_RE, m => { ch = true; return matchCase(m, ANTO_MAP[m.toLowerCase()]); });
  return ch ? s : null;
}

export function fnOptions(correct) {
  const opts = [correct];
  const opp = opposite(correct);
  if (opp && opp !== correct) opts.push(opp);
  for (const f of shuffle(FN_POOL.filter(f => f !== correct && f !== opp))) {
    if (opts.length >= 4) break;
    if (!opts.includes(f)) opts.push(f);
  }
  return shuffle(opts.slice(0, 4));
}

export function buildMuscleAccept(name) {
  const exact = new Set();
  const add = s => { const n = normMuscle(s); if (n) exact.add(n); };
  const base = name.replace(/\(.*?\)/g, " ").replace(/\s+/g, " ").trim();
  const key = normMuscle(base);
  (ABBR_MUSCLE[key] || []).forEach(add);
  const words = key.split(" ").filter(w => w.length > 2);
  if (words.length >= 2) add(words.map(w => w[0]).join(""));
  const fuzzy = [normMuscle(name), key].filter((v, i, arr) => arr.indexOf(v) === i);
  return { exact, fuzzy };
}

export function matchMuscle(v, m) {
  return m.exact.has(v) || fuzzyHit(v, m.fuzzy);
}
